/** Fetchers — jednotlivé volání DB views/tabulek pro TanStack Query.
 *  RLS na backendu zaručí, že vidíme jen vlastní data. */

import { supabase } from "./supabase";
import type {
  PartnerBalanceAgingRow,
  PartnerDashboardSummary,
  PartnerInvoice,
  PartnerOrder,
} from "./types";

const ORDER_FIELDS =
  "id, deal_number, partner_id, billing_entity_id, end_client_person_id, title, description, " +
  "scheduled_start_time, scheduled_end_date, " +
  "scheduled_date, installation_contact_name, installation_contact_phone, " +
  "installation_street, installation_city, installation_zip, installation_country, " +
  "installation_gps_lat, installation_gps_lng, value_eur, system_type, device_label, " +
  "ab_signed_at, open_complaints_count, status_label, created_at, updated_at";

export async function fetchDashboardSummary(): Promise<PartnerDashboardSummary | null> {
  const { data, error } = await supabase
    .from("partner_dashboard_summary")
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data as PartnerDashboardSummary | null;
}

export interface OrdersFilter {
  fromDate?: string; // YYYY-MM-DD inclusive
  toDate?: string; // YYYY-MM-DD inclusive
  statuses?: PartnerOrder["status_label"][];
  systems?: string[];
  search?: string;
  limit?: number;
  orderBy?: { column: keyof PartnerOrder; ascending?: boolean };
}

export async function fetchOrders(filter: OrdersFilter = {}): Promise<PartnerOrder[]> {
  let q = supabase.from("partner_orders_view").select(ORDER_FIELDS);

  if (filter.fromDate) q = q.gte("scheduled_date", filter.fromDate);
  if (filter.toDate) q = q.lte("scheduled_date", filter.toDate);
  if (filter.statuses && filter.statuses.length) {
    q = q.in("status_label", filter.statuses);
  }
  if (filter.systems && filter.systems.length) {
    q = q.in("system_type", filter.systems);
  }
  if (filter.search) {
    // ilike na title, installation_city, end-client name jde řešit přes fulltext;
    // pro v0.1 jednoduchý fallback: title + city
    const s = `%${filter.search}%`;
    q = q.or(`title.ilike.${s},installation_city.ilike.${s}`);
  }

  const sortCol = (filter.orderBy?.column as string) || "scheduled_date";
  const ascending = filter.orderBy?.ascending ?? false;
  q = q.order(sortCol, { ascending, nullsFirst: false });

  if (filter.limit) q = q.limit(filter.limit);

  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as unknown as PartnerOrder[];
}

export async function fetchOrderById(idOrNumber: string): Promise<PartnerOrder | null> {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrNumber);
  const column = isUuid ? "id" : "deal_number";
  const { data, error } = await supabase
    .from("partner_orders_view")
    .select(ORDER_FIELDS)
    .eq(column, idOrNumber)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as PartnerOrder | null;
}

/** Joined query: order + end-client jméno z persons */
export async function enrichOrdersWithEndClient(orders: PartnerOrder[]): Promise<PartnerOrder[]> {
  const personIds = Array.from(
    new Set(orders.map((o) => o.end_client_person_id).filter(Boolean) as string[]),
  );
  if (!personIds.length) return orders;

  const { data: persons } = await supabase
    .from("persons")
    .select("id, first_name, last_name, company_name")
    .in("id", personIds);

  const lookup = new Map<string, { first?: string; last?: string; company?: string }>();
  (persons || []).forEach((p) => {
    lookup.set((p as { id: string }).id, {
      first: (p as { first_name?: string }).first_name,
      last: (p as { last_name?: string }).last_name,
      company: (p as { company_name?: string }).company_name,
    });
  });

  return orders.map((o) => {
    if (!o.end_client_person_id) return o;
    const meta = lookup.get(o.end_client_person_id);
    if (!meta) return o;
    const personalName = [meta.first, meta.last].filter(Boolean).join(" ");
    const isCompany = !!meta.company;
    const label = isCompany
      ? meta.company || personalName
      : meta.last
        ? `Familie ${meta.last}`
        : personalName;
    return {
      ...o,
      end_client_name: personalName,
      end_client_label: label || o.title || "—",
    };
  });
}

export async function fetchRecentInvoices(limit = 5): Promise<PartnerInvoice[]> {
  const { data, error } = await supabase
    .from("invoices")
    .select(
      "id, invoice_number, invoice_type, status, net_amount, currency, issued_at, due_at, paid_at, deal_id",
    )
    .order("issued_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return ((data || []) as PartnerInvoice[]).map(decorateInvoice);
}

export interface InvoicesFilter {
  invoiceType?: "advance" | "final";
  paidOnly?: boolean;
  openOnly?: boolean;
}

export async function fetchAllInvoices(filter: InvoicesFilter = {}): Promise<PartnerInvoice[]> {
  let q = supabase
    .from("invoices")
    .select(
      "id, invoice_number, invoice_type, status, net_amount, currency, issued_at, due_at, paid_at, deal_id, pdf_document_id",
    );
  if (filter.invoiceType) q = q.eq("invoice_type", filter.invoiceType);
  if (filter.paidOnly) q = q.eq("status", "paid");
  if (filter.openOnly) q = q.in("status", ["issued", "sent"]);
  q = q.order("issued_at", { ascending: false, nullsFirst: false });

  const { data, error } = await q;
  if (error) throw error;
  return (
    (data || []) as unknown as Array<PartnerInvoice & { pdf_document_id?: string | null }>
  ).map(decorateInvoice);
}

export async function fetchBalanceAging(): Promise<PartnerBalanceAgingRow[]> {
  const { data, error } = await supabase
    .from("partner_balance_aging")
    .select("*")
    .order("age_days", { ascending: false });
  if (error) throw error;
  return (data || []) as PartnerBalanceAgingRow[];
}

function decorateInvoice(inv: PartnerInvoice): PartnerInvoice {
  const kind = inv.invoice_type === "advance" ? "Anzahlung" : "Schlussrechnung";
  let display: PartnerInvoice["display_status"] = undefined;
  if (inv.status === "paid") display = "bezahlt";
  else if (inv.due_at && new Date(inv.due_at) < new Date() && !inv.paid_at) display = "überfällig";
  else if (inv.status === "issued" || inv.status === "sent") display = "offen";

  return { ...inv, kind_label: kind, display_status: display };
}

// ────────────────────────────────────────────────────────────────
// Detail-page fetchers
// ────────────────────────────────────────────────────────────────

export interface DealPhoto {
  name: string; // storage object name (relative to bucket)
  url: string; // signed URL (10 min)
  size: number;
  created_at: string | null;
}

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|gif|heic|heif)$/i;

async function listFolderRecursive(
  bucket: string,
  prefix: string,
  depth = 0,
): Promise<{ path: string; size: number; created_at: string | null }[]> {
  if (depth > 4) return [];
  const { data: list, error } = await supabase.storage
    .from(bucket)
    .list(prefix, { limit: 200, sortBy: { column: "name", order: "asc" } });
  if (error || !list) return [];

  const out: { path: string; size: number; created_at: string | null }[] = [];
  for (const item of list) {
    const itemAny = item as { id?: string | null; metadata?: { size?: number } };
    const fullPath = `${prefix}/${item.name}`;
    // Folders v Supabase Storage maji id === null
    if (itemAny.id === null || itemAny.id === undefined) {
      const sub = await listFolderRecursive(bucket, fullPath, depth + 1);
      out.push(...sub);
    } else {
      out.push({
        path: fullPath,
        size: itemAny.metadata?.size ?? 0,
        created_at: item.created_at ?? null,
      });
    }
  }
  return out;
}

export async function fetchDealPhotos(dealId: string): Promise<DealPhoto[]> {
  const folders = [dealId, `deals/${dealId}`];
  const all: { path: string; size: number; created_at: string | null }[] = [];
  for (const prefix of folders) {
    const items = await listFolderRecursive("deal-photos", prefix);
    all.push(...items);
  }
  // jen obrazky
  const images = all.filter((f) => IMAGE_EXT_RE.test(f.path));
  if (images.length === 0) return [];

  const results: DealPhoto[] = [];
  for (const img of images) {
    const { data: signed } = await supabase.storage
      .from("deal-photos")
      .createSignedUrl(img.path, 60 * 10);
    if (!signed) continue;
    results.push({
      name: img.path,
      url: signed.signedUrl,
      size: img.size,
      created_at: img.created_at,
    });
  }
  return results;
}

export interface DealDocument {
  id: string;
  file_name: string;
  mime_type: string | null;
  category: string | null;
  /** Zdroj — určuje jak otevřít: storage = direct signed URL,
   *  document/invoice/contract = přes pdf-link edge fn (RLS gated). */
  source: "storage" | "document" | "invoice" | "contract";
  storage_path?: string;
  storage_bucket?: string;
  document_id?: string;
  created_at: string;
  size: number | null;
  label_de: string;
}

const DOC_LABEL_BY_TYPE: Record<string, string> = {
  auftragsbestaetigung: "Auftragsbestätigung",
  ab: "Auftragsbestätigung",
  rahmenvertrag: "Rahmenvertrag",
  technicky_list: "Technisches Datenblatt",
  tech_list_tc: "Technisches Datenblatt — Wärmepumpe",
  hydro_schema: "Hydraulisches Schema",
  elektro_schema: "Elektroschema",
  zfr: "Anzahlungsrechnung",
  fa: "Schlussrechnung",
  invoice_advance: "Anzahlungsrechnung",
  invoice_final: "Schlussrechnung",
  jine: "Sonstige Unterlagen",
};

function labelFromFileName(name: string): string {
  const lower = name.toLowerCase();
  for (const [key, label] of Object.entries(DOC_LABEL_BY_TYPE)) {
    if (lower.includes(key)) return label;
  }
  return name;
}

const PDF_EXT_RE = /\.(pdf)$/i;

export async function fetchDealDocuments(dealId: string): Promise<DealDocument[]> {
  const out: DealDocument[] = [];

  // 1) Storage PDFs — deal-photos/{dealId}/docs/* a deal-photos/deals/{dealId}/docs/*
  for (const prefix of [`${dealId}/docs`, `deals/${dealId}/docs`]) {
    const items = await listFolderRecursive("deal-photos", prefix);
    for (const it of items) {
      if (!PDF_EXT_RE.test(it.path)) continue;
      const fname = it.path.split("/").pop() || it.path;
      out.push({
        id: `storage:${it.path}`,
        file_name: fname,
        mime_type: "application/pdf",
        category: null,
        source: "storage",
        storage_path: it.path,
        storage_bucket: "deal-photos",
        created_at: it.created_at || new Date().toISOString(),
        size: it.size,
        label_de: labelFromFileName(fname),
      });
    }
  }

  // 2) Documents tabulka (deal_id link)
  const { data: docs } = await supabase
    .from("documents")
    .select(
      "id, file_name, mime_type, document_type, storage_path, s3_bucket, s3_key, created_at, file_size_bytes",
    )
    .eq("deal_id", dealId);
  for (const d of (docs || []) as unknown as Array<{
    id: string;
    file_name: string;
    mime_type: string | null;
    document_type: string | null;
    created_at: string;
    file_size_bytes: number | null;
  }>) {
    out.push({
      id: d.id,
      file_name: d.file_name,
      mime_type: d.mime_type,
      category: d.document_type,
      source: "document",
      document_id: d.id,
      created_at: d.created_at,
      size: d.file_size_bytes,
      label_de: DOC_LABEL_BY_TYPE[d.document_type || ""] || labelFromFileName(d.file_name),
    });
  }

  // 3) Faktury (invoice.pdf_document_id)
  const { data: invs } = await supabase
    .from("invoices")
    .select("id, invoice_number, invoice_type, pdf_document_id, issued_at")
    .eq("deal_id", dealId)
    .not("pdf_document_id", "is", null);
  for (const inv of (invs || []) as unknown as Array<{
    invoice_number: string;
    invoice_type: string;
    pdf_document_id: string;
    issued_at: string | null;
  }>) {
    const label = inv.invoice_type === "advance" ? "Anzahlungsrechnung" : "Schlussrechnung";
    out.push({
      id: `inv:${inv.pdf_document_id}`,
      file_name: `${inv.invoice_number}.pdf`,
      mime_type: "application/pdf",
      category: "invoice",
      source: "invoice",
      document_id: inv.pdf_document_id,
      created_at: inv.issued_at || new Date().toISOString(),
      size: null,
      label_de: label,
    });
  }

  // 4) Podepsaný AB (customer_contracts.signed_pdf_document_id)
  const { data: ccs } = await supabase
    .from("customer_contracts")
    .select("id, signed_at, signed_pdf_document_id")
    .eq("deal_id", dealId)
    .not("signed_pdf_document_id", "is", null);
  for (const cc of (ccs || []) as unknown as Array<{
    signed_at: string | null;
    signed_pdf_document_id: string;
  }>) {
    out.push({
      id: `cc:${cc.signed_pdf_document_id}`,
      file_name: `Auftragsbestaetigung_${cc.signed_at?.slice(0, 10) || "signed"}.pdf`,
      mime_type: "application/pdf",
      category: "contract",
      source: "contract",
      document_id: cc.signed_pdf_document_id,
      created_at: cc.signed_at || new Date().toISOString(),
      size: null,
      label_de: "Auftragsbestätigung (signed)",
    });
  }

  const seen = new Set<string>();
  return out
    .filter((d) => {
      const key = d.document_id || d.storage_path || d.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
}

export interface DealPaymentStep {
  id: string;
  label: string;
  percent: number | null;
  fixed_amount: number | null;
  invoice_type: "advance" | "final" | string;
  status: string;
  due_in_days: number | null;
  sort_order: number;
}

export async function fetchDealPaymentSteps(dealId: string): Promise<DealPaymentStep[]> {
  const { data: ccs } = await supabase
    .from("customer_contracts")
    .select("id")
    .eq("deal_id", dealId);
  const ids = (ccs || []).map((c) => (c as { id: string }).id);
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from("customer_contract_payment_steps")
    .select("id, label, percent, fixed_amount, invoice_type, status, due_in_days, sort_order")
    .in("customer_contract_id", ids)
    .order("sort_order");
  if (error) throw error;
  return (data || []) as unknown as DealPaymentStep[];
}

export interface DealComplaint {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  priority: "low" | "normal" | "high" | "urgent";
  status: string;
  reported_at: string;
  resolved_at: string | null;
  closed_at: string | null;
}

export async function fetchDealComplaints(dealId: string): Promise<DealComplaint[]> {
  const { data, error } = await supabase
    .from("service_tickets")
    .select(
      "id, ticket_number, subject, description, priority, status, reported_at, resolved_at, closed_at",
    )
    .eq("deal_id", dealId)
    .eq("category", "complaint")
    .order("reported_at", { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as DealComplaint[];
}

export interface CreateComplaintInput {
  dealId: string;
  category: "tech" | "delivery" | "docs" | "other";
  description: string;
  urgency: "low" | "normal" | "high" | "urgent";
  partnerEmail: string;
}

const CATEGORY_SUBJECT: Record<CreateComplaintInput["category"], string> = {
  tech: "Reklamation: Technisches Problem",
  delivery: "Reklamation: Lieferung",
  docs: "Reklamation: Dokumentation",
  other: "Reklamation: Sonstiges",
};

export async function createComplaint(input: CreateComplaintInput): Promise<DealComplaint> {
  const { data, error } = await supabase
    .from("service_tickets")
    .insert({
      deal_id: input.dealId,
      category: "complaint",
      priority: input.urgency,
      status: "new",
      subject: CATEGORY_SUBJECT[input.category],
      description: input.description,
      reported_by_email: input.partnerEmail,
    })
    .select(
      "id, ticket_number, subject, description, priority, status, reported_at, resolved_at, closed_at",
    )
    .single();
  if (error) throw error;
  return data as unknown as DealComplaint;
}

// ────────────────────────────────────────────────────────────────
// Map data
// ────────────────────────────────────────────────────────────────

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  kind: "active" | "planned" | "planned_far" | "done";
  client: string | null;
  city: string | null;
  value: number | null;
  status: PartnerOrder["status_label"];
  title: string | null;
  scheduled_date: string | null;
  installation_address?: string | null;
}

const MAP_PIN_FIELDS =
  "id, installation_gps_lat, installation_gps_lng, status_label, value_eur, " +
  "title, installation_city, installation_zip, installation_street, installation_country, " +
  "scheduled_date, end_client_person_id";

/** Vrátí raw pins (bez geocode) — zahrnuje vše s adresou nebo GPS, klasifikovano
 *  podle časového okna (done/active/planned/planned_far). GPS dosadí později
 *  klient přes geocodeAddress() s localStorage cache. */
export async function fetchMapPins(): Promise<MapPin[]> {
  const ninetyAgo = new Date();
  ninetyAgo.setDate(ninetyAgo.getDate() - 90);
  const halfYearAhead = new Date();
  halfYearAhead.setDate(halfYearAhead.getDate() + 180);

  const { data, error } = await supabase
    .from("partner_orders_view")
    .select(MAP_PIN_FIELDS)
    .gte("scheduled_date", toDateOnly(ninetyAgo))
    .lte("scheduled_date", toDateOnly(halfYearAhead));
  if (error) throw error;

  type Row = {
    id: string;
    installation_gps_lat: number | null;
    installation_gps_lng: number | null;
    status_label: PartnerOrder["status_label"];
    value_eur: number | null;
    title: string | null;
    installation_city: string | null;
    installation_zip: string | null;
    installation_street: string | null;
    installation_country: string | null;
    scheduled_date: string | null;
    end_client_person_id: string | null;
  };
  const rows = (data || []) as unknown as Row[];

  const personIds = Array.from(
    new Set(rows.map((r) => r.end_client_person_id).filter(Boolean) as string[]),
  );
  const personLookup = new Map<string, string>();
  if (personIds.length) {
    const { data: persons } = await supabase
      .from("persons")
      .select("id, first_name, last_name, company_name")
      .in("id", personIds);
    (persons || []).forEach((p) => {
      const r = p as { id: string; first_name?: string; last_name?: string; company_name?: string };
      const label = r.company_name || (r.last_name ? `Familie ${r.last_name}` : "");
      if (label) personLookup.set(r.id, label);
    });
  }

  // Časové buckety
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - ((today.getDay() || 7) - 1));
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const nextWeekStart = new Date(weekStart);
  nextWeekStart.setDate(weekStart.getDate() + 7);
  const nextWeekEnd = new Date(weekStart);
  nextWeekEnd.setDate(weekStart.getDate() + 13);

  return rows
    .filter((r) => {
      // Pin musí mít adresu nebo GPS — bez ničeho ho nezobrazíme
      const hasGps = r.installation_gps_lat != null && r.installation_gps_lng != null;
      const hasAddr =
        (r.installation_street && r.installation_street.trim()) ||
        (r.installation_city && r.installation_city.trim());
      return hasGps || hasAddr;
    })
    .map((r): MapPin => {
      const sched = r.scheduled_date ? new Date(r.scheduled_date) : null;
      let kind: MapPin["kind"] = "planned_far";
      if (r.status_label === "abgeschlossen" || (sched && sched < today)) {
        kind = "done";
      } else if (sched && sched >= weekStart && sched <= weekEnd) {
        kind = "active";
      } else if (sched && sched >= nextWeekStart && sched <= nextWeekEnd) {
        kind = "planned";
      } else if (sched && sched > nextWeekEnd) {
        kind = "planned_far";
      } else {
        kind = "planned";
      }
      return {
        id: r.id,
        lat: r.installation_gps_lat != null ? Number(r.installation_gps_lat) : NaN,
        lng: r.installation_gps_lng != null ? Number(r.installation_gps_lng) : NaN,
        kind,
        client: r.end_client_person_id ? personLookup.get(r.end_client_person_id) || null : null,
        city: r.installation_city,
        value: r.value_eur,
        status: r.status_label,
        title: r.title,
        scheduled_date: r.scheduled_date,
        installation_address: [
          r.installation_street,
          [r.installation_zip, r.installation_city].filter(Boolean).join(" "),
          r.installation_country || "Deutschland",
        ]
          .filter((s) => s && s.trim().length > 0)
          .join(", "),
      };
    });
}

// ────────────────────────────────────────────────────────────────
// Geocoding cache (localStorage) přes Nominatim
// ────────────────────────────────────────────────────────────────

const GEOCODE_CACHE_KEY = "warmteo-portal-geocode-v1";
type GeocodeCache = Record<string, { lat: number; lng: number; t: number } | null>;

function loadGeocodeCache(): GeocodeCache {
  try {
    return JSON.parse(localStorage.getItem(GEOCODE_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveGeocodeCache(cache: GeocodeCache) {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota
  }
}

let geocodeQueue: Promise<unknown> = Promise.resolve();
function throttledGeocode(query: string): Promise<{ lat: number; lng: number } | null> {
  geocodeQueue = geocodeQueue.then(
    () =>
      new Promise<{ lat: number; lng: number } | null>((resolve) => {
        setTimeout(async () => {
          try {
            const r = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
              { headers: { "Accept-Language": "de" } },
            );
            const arr = await r.json();
            if (Array.isArray(arr) && arr.length) {
              resolve({ lat: Number(arr[0].lat), lng: Number(arr[0].lon) });
            } else {
              resolve(null);
            }
          } catch {
            resolve(null);
          }
        }, 1100); // Nominatim 1 req/s
      }),
  );
  return geocodeQueue as Promise<{ lat: number; lng: number } | null>;
}

export async function geocodePins(pins: MapPin[]): Promise<MapPin[]> {
  const cache = loadGeocodeCache();
  const result: MapPin[] = [];
  let dirty = false;

  for (const pin of pins) {
    if (Number.isFinite(pin.lat) && Number.isFinite(pin.lng)) {
      result.push(pin);
      continue;
    }
    const addr = pin.installation_address?.trim();
    if (!addr) continue;
    if (cache[addr]) {
      const c = cache[addr]!;
      result.push({ ...pin, lat: c.lat, lng: c.lng });
      continue;
    }
    if (cache[addr] === null) continue; // already attempted, no result
    const geo = await throttledGeocode(addr);
    cache[addr] = geo ? { lat: geo.lat, lng: geo.lng, t: Date.now() } : null;
    dirty = true;
    if (geo) result.push({ ...pin, lat: geo.lat, lng: geo.lng });
  }
  if (dirty) saveGeocodeCache(cache);
  return result;
}

export interface PartnerVehiclePin {
  vehicle_id: string;
  lat: number;
  lng: number;
  recorded_at: string;
  speed_kmh: number;
  vehicle_label: string | null;
  deal_id: string;
  deal_title: string | null;
  installation_city: string | null;
}

export async function fetchPartnerVehicles(): Promise<PartnerVehiclePin[]> {
  const { data, error } = await supabase.from("partner_visible_vehicles").select("*");
  if (error) {
    // View nemusí existovat ve dev DB — vrať prázdné
    if (/relation .* does not exist/i.test(error.message || "")) return [];
    throw error;
  }
  return (data || []) as unknown as PartnerVehiclePin[];
}

// ────────────────────────────────────────────────────────────────
// TČ konfigurace (Neue Anfrage form)
// ────────────────────────────────────────────────────────────────

export interface TcConfig {
  id: string;
  brand: string;
  model: string;
  package_unit: string | null;
  system_type: string | null;
  power_kw: number | null;
}

export async function fetchTcConfigs(): Promise<TcConfig[]> {
  const { data, error } = await supabase
    .from("tc_system_configs")
    .select("id, brand, model, package_unit, system_type, power_kw, is_active, sort_order")
    .eq("is_active", true)
    .order("brand")
    .order("model")
    .order("sort_order");
  if (error) throw error;
  return (data || []) as unknown as TcConfig[];
}

/** Vrátí start (Mo) a end (So) ISO týdne pro daný offset (0 = aktuální). */
export function isoWeekRange(offsetWeeks = 0): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay() || 7; // 1..7 (Mo..So)
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - (day - 1) + offsetWeeks * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

export function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}
